import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import ITSVG from 'common/helpers/ITSVG';
import { StatusFilter } from 'common/types/status-filter.model';
import filterIcon from 'assets/images/icons/filter.svg';
import { DepartamentsModel } from 'app/departaments/model/departaments.model';
import { DepartamentsService } from 'app/departaments/services/departaments.service';
import en from '../../i18n/en-us.json';
import pt from '../../i18n/pt-br.json';
import es from '../../i18n/es-es.json';

interface ProcessesFilterDropdownProps {
  fetchProcesses: (departmentId?: number, status?: string) => Promise<void>;
  statusFilter: string;
}

export function ProcessesFilterDropdown({ fetchProcesses, statusFilter }: ProcessesFilterDropdownProps) {
  const { t } = useTranslation();
  i18next.addResourceBundle('us', 'translation', en);
  i18next.addResourceBundle('br', 'translation', pt);
  i18next.addResourceBundle('es', 'translation', es);
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartaments] = useState<DepartamentsModel[]>([]);
  const [openDepartmentList, setOpenDepartmentList] = useState<boolean>(false);
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [status, setStatus] = useState<string>('');
  const loadingDepartmentList = openDepartmentList && isLoading && departments.length === 0;

  const handleFilterSubmit = () => {
    fetchProcesses(departmentId ?? undefined, status)
  }

  const fetchDepartaments = async () => {
    setIsLoading(true);
    const response = await DepartamentsService.getDataList(StatusFilter.Active);
    if (response && !response.hasErrors) {
      setDepartaments(response.data);
    } else {
      toast.error(`${t('crud.read.errorMessage')}`);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchDepartaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <button
        type='button'
        className='btn btn-sm btn-flex btn-light btn-active-primary fw-bolder'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
        data-kt-menu-flip='top-end'
      >
        <ITSVG path={filterIcon} className='svg-icon-5 svg-icon-gray-500 me-1' />
        {t('generalMessages.filter')}
      </button>
      <div className='menu menu-sub menu-sub-dropdown w-250px w-md-300px' data-kt-menu='true'>
        <div className='px-7 py-5'>
          <div className='fs-5 text-dark fw-bolder'>{t('toolbar.filter.title')}</div>
        </div>
        <div className='separator border-gray-200'></div>
        <div className='px-7 py-5'>
          <div className='mb-10'>
            <label className='form-label fw-bold'>{t('toolbar.filter.options.status.label')}</label>
            <Autocomplete
              id='status'
              options={[
                { label: t('generalMessages.all'), value: '' },
                { label: t('generalMessages.active'), value: 'true' },
                { label: t('generalMessages.inactive'), value: 'false' }
              ]}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              defaultValue={{ label: t('generalMessages.active'), value: 'true' }}
              onChange={(e, value) => {
                e.stopPropagation();
                if (!value) {
                  setStatus('');
                } else {
                  setStatus(value.value);
                }
              }}
              renderInput={(params) =>
                <TextField
                  {...params}
                  placeholder={`${t('generalMessages.select')}`}
                />
              }
            />
          </div>

          <div className='mb-10'>
            <label className='form-label fw-bold'>{t('toolbar.filter.options.department.label')}</label>
            <Autocomplete
              id='departments'
              options={departments}
              getOptionLabel={option => option.name ? option.name : ""}
              onChange={(e, value) => {
                e.stopPropagation();
                if (!value) {
                  setDepartmentId(null);
                } else {
                  setDepartmentId(value?.id);
                }
              }}
              open={openDepartmentList}
              onOpen={() => {
                setOpenDepartmentList(true);
              }}
              onClose={() => {
                setOpenDepartmentList(false);
              }}
              renderInput={(params) =>
                <TextField
                  {...params}
                  placeholder={`${t('generalMessages.select')}`}
                />
              }
              loading={loadingDepartmentList}
              loadingText={<div className="text-center"><CircularProgress size={14} /></div>}
              noOptionsText={t('toolbar.filter.options.department.noDataMessage')}
            />
          </div>

          <div className='d-flex justify-content-end'>
            <button
              type='reset'
              className='btn btn-sm btn-white btn-active-light-primary me-2'
              data-kt-menu-dismiss='true'
            >
              {t('generalMessages.cancel')}
            </button>
            <button
              type='button'
              onClick={handleFilterSubmit}
              className='btn btn-sm btn-primary'
              data-kt-menu-dismiss='true'
              disabled={isLoading}
            >
              {t('generalMessages.apply')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
